// src/lib/apiFetch.js
import { supabase } from "../supabaseClient";
import { ensureFreshSession, isInvalidRefreshTokenError, getCachedSession, isAuthReady } from "../contexts/AuthContext";

let baseLogPrinted = false;

let warnedBaseMismatch = false;

// In-memory cached token to avoid calling supabase.auth.getSession() on every request.
let cachedAccessToken = null;
let authStateSubRegistered = false;

// Track if we've logged the fallback warning once per page load
let fallbackWarningLogged = false;

// Track if we've notified user about Supabase being unhealthy
let supabaseUnhealthyNotified = false;

function isPublicApi(url) {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.startsWith("/api/public/");
  } catch {
    return String(url).startsWith("/api/public/");
  }
}

/**
 * Wait up to maxMs for auth to become ready, polling every intervalMs.
 * Returns true if auth became ready, false if timeout.
 */
async function waitForAuthReady(maxMs = 300, intervalMs = 50) {
  const start = Date.now();
  while (!isAuthReady()) {
    if (Date.now() - start >= maxMs) return false;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return true;
}

function ensureAuthStateSubscription() {
  if (authStateSubRegistered) return;
  if (!supabase?.auth?.onAuthStateChange) return;

  authStateSubRegistered = true;
  try {
    supabase.auth.onAuthStateChange((_event, session) => {
      cachedAccessToken = session?.access_token ?? null;
    });
  } catch {
    // best-effort only
  }
}

const warnedApi404 = new Set();
function warnApi404Once(routeKey) {
  if (!import.meta.env.DEV) return;
  const key = String(routeKey || "");
  if (!key) return;
  if (warnedApi404.has(key)) return;
  warnedApi404.add(key);
  // eslint-disable-next-line no-console
  console.warn(
    `[apiFetch] 404 for ${key}. Vercel API routes aren't running. Use vercel dev or configure dev proxy.`
  );
}

// Notify user that Supabase is temporarily unavailable (transient outage)
function notifySupabaseUnhealthy(message = "Service temporarily unavailable. Retrying...") {
  if (supabaseUnhealthyNotified) return; // Only notify once per session
  supabaseUnhealthyNotified = true;

  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("mudo:toast", {
          detail: {
            message,
            tone: "warning",
            ttl: 5000,
            dismissible: true,
          },
        })
      );
    }
  } catch (err) {
    console.warn("[apiFetch] Failed to dispatch toast", err);
  }
}

// Check if error is a transient failure (should retry)
function isTransientError(err, status) {
  const msg = String(err?.message || "").toLowerCase();
  return (
    status === 522 ||
    status === 503 ||
    status === 504 ||
    msg.includes("failed to fetch") ||
    msg.includes("network") ||
    err?.name === "TypeError"
  );
}

// Retry with exponential backoff: 300ms, 900ms, 2000ms (3 total attempts)
async function fetchWithRetry(url, options, maxAttempts = 3) {
  const backoffMs = [300, 900, 2000];
  let lastError = null;
  let lastStatus = 0;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await fetch(url, options);

      // Success (2xx) - return immediately
      if (res.ok) {
        // Reset notification flag on success
        supabaseUnhealthyNotified = false;
        return res;
      }

      // Check if status is transient
      lastStatus = res.status;
      const isTransient = isTransientError(null, res.status);

      if (!isTransient || attempt >= maxAttempts - 1) {
        // Not transient, or exhausted retries - return as-is
        return res;
      }

      // Transient error, retry after backoff
      notifySupabaseUnhealthy("Service temporarily unavailable. Retrying...");
      const waitMs = backoffMs[attempt] || 2000;
      if (import.meta.env.DEV) {
        console.warn(`[apiFetch] transient ${res.status}, retrying after ${waitMs}ms`, { url });
      }
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    } catch (err) {
      // Network error (Failed to fetch, etc.)
      lastError = err;
      const isTransient = isTransientError(err, 0);

      if (!isTransient || attempt >= maxAttempts - 1) {
        // Not transient, or exhausted retries - throw immediately
        throw err;
      }

      // Transient network error, retry after backoff
      notifySupabaseUnhealthy("Network error. Retrying...");
      const waitMs = backoffMs[attempt] || 2000;
      if (import.meta.env.DEV) {
        console.warn(`[apiFetch] network error, retrying after ${waitMs}ms`, { url, error: err.message });
      }
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
  }

  // Exhausted retries
  if (lastError) throw lastError;
  throw new Error(`Request failed with status ${lastStatus}`);
}

export async function apiFetch(url, options = {}) {
  ensureAuthStateSubscription();

  const envBaseRaw = import.meta.env.VITE_API_BASE_URL;
  const envBase = typeof envBaseRaw === "string" ? envBaseRaw.trim() : "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const base = envBase || origin;

  if (!baseLogPrinted) {
    const baseLabel = base || "(relative same-origin)";
    console.log(`[apiFetch] base URL: ${baseLabel}`);
    baseLogPrinted = true;

    if (import.meta.env.DEV && !warnedBaseMismatch && envBase && origin) {
      try {
        const envOrigin = new URL(envBase).origin;
        if (envOrigin && envOrigin !== origin) {
          warnedBaseMismatch = true;
          // eslint-disable-next-line no-console
          console.warn(
            `[apiFetch] VITE_API_BASE_URL origin (${envOrigin}) differs from window.location.origin (${origin}). Same-origin /api/* calls will not hit this app's origin.`
          );
        }
      } catch {
        // ignore invalid env base
      }
    }
  }

  let resolvedUrl = url;

  // If input url is already absolute (http/https), do not modify it.
  // If input url starts with "/api/", build final url as `${base}${url}`.
  if (typeof url === "string" && /^https?:\/\//i.test(url)) {
    resolvedUrl = url;
  } else if (base && typeof url === "string" && url.startsWith("/api/")) {
    resolvedUrl = `${base}${url}`;
  }

  const makeHeaders = () => ({
    "Content-Type": "application/json",
    ...(options.headers || {}),
  });

  // Attach token for same-origin (or relative) API calls.
  let shouldAttachToken = false;
  if (typeof window !== "undefined" && typeof resolvedUrl === "string") {
    if (resolvedUrl.startsWith("/")) {
      shouldAttachToken = true;
    } else if (/^https?:\/\//i.test(resolvedUrl)) {
      try {
        shouldAttachToken = new URL(resolvedUrl).origin === window.location.origin;
      } catch {
        shouldAttachToken = false;
      }
    }
  }

  // Determine credentials (default: same-origin)
  let credentials = options.credentials;
  if (!credentials) credentials = "same-origin";

  for (let attempt = 0; attempt < 2; attempt++) {
    const headers = makeHeaders();

    if (shouldAttachToken) {
      if (cachedAccessToken) {
        headers.Authorization = `Bearer ${cachedAccessToken}`;
        if (import.meta.env.DEV) {
          console.log(`[apiFetch] auth header set for ${resolvedUrl}`);
        }
      } else {
        // Use centralized auth state instead of direct supabase.auth.getSession() call
        // to avoid slow 10-13s GET /user network calls
        try {
          // If auth isn't ready yet, wait up to 300ms for it to initialize
          if (!isAuthReady()) {
            const becameReady = await waitForAuthReady(300, 50);
            if (!becameReady && import.meta.env.DEV && !fallbackWarningLogged) {
              fallbackWarningLogged = true;
              console.warn("[apiFetch] Auth not ready after 300ms wait, proceeding without token");
            }
          }

          // Get session from centralized auth state (no network call)
          const session = getCachedSession();
          if (session?.access_token) {
            cachedAccessToken = session.access_token;
            headers.Authorization = `Bearer ${session.access_token}`;
            if (import.meta.env.DEV) {
              console.log(`[apiFetch] auth header set from cached session for ${resolvedUrl}`);
            }
          } else {
            if (import.meta.env.DEV) {
              console.warn(
                "[apiFetch] No session available in cached auth state - Authorization header will not be sent"
              );
            }
          }
        } catch (err) {
          console.warn("[apiFetch] Failed to get cached session:", err?.message);
        }
      }
    }

    // Never send Authorization to public API routes (prevents RLS authenticated path + timeouts)
    if (isPublicApi(resolvedUrl)) {
      if (headers && "Authorization" in headers) {
        delete headers.Authorization;
      }
    }

    // Use fetchWithRetry for automatic retry on transient failures (522/503/504)
    const res = await fetchWithRetry(resolvedUrl, {
      ...options,
      credentials,
      headers,
    });

    // DEV-only hint for missing local API routes (warn once per route)
    if (import.meta.env.DEV && res.status === 404) {
      try {
        const routeKey =
          typeof url === "string" && url.startsWith("/api/")
            ? url
            : (() => {
                const u = new URL(resolvedUrl, typeof window !== "undefined" ? window.location.origin : "http://localhost");
                return u.pathname.startsWith("/api/") ? u.pathname : "";
              })();
        if (routeKey) warnApi404Once(routeKey);
      } catch {
        // ignore
      }
    }

    // Retry once on auth failures by refreshing session on-demand.
    if ((res.status === 401 || res.status === 403) && attempt === 0 && shouldAttachToken) {
      try {
        const refreshed = await ensureFreshSession({ reason: "401 retry" });
        if (refreshed) {
          try {
            console.warn("[apiFetch] retrying once after ensureFreshSession", {
              status: res.status,
              url: resolvedUrl,
            });
          } catch {}
          continue;
        }
      } catch (e) {
        // If invalid refresh token, the global handler already triggered signout/redirect
        // Just propagate the error
        if (isInvalidRefreshTokenError(e)) {
          throw e;
        }
        // Auth is likely degraded (e.g. 522 / Failed to fetch). Do not retry.
      }
    }

    // Always read body as text first, then try to parse JSON
    let rawText = null;
    try {
      rawText = await res.clone().text();
    } catch {
      rawText = null;
    }

    let parsed = null;
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    const looksJson = ct.includes("application/json") || (rawText && /^[\s]*[\[{]/.test(rawText));
    if (looksJson && rawText) {
      try {
        parsed = JSON.parse(rawText);
      } catch {
        parsed = null;
      }
    }

    if (!res.ok) {
      const err = new Error(
        (parsed && (parsed.error || parsed.message)) ||
          res.statusText ||
          "Request failed"
      );
      const code = parsed?.code || parsed?.error?.code || null;
      const reqId = parsed?.reqId || parsed?.req_id || null;

      err.status = res.status;
      err.statusText = res.statusText;
      err.url = res.url;
      err.method = (options && options.method) || "GET";
      err.data = parsed ?? rawText ?? null;
      err.raw = rawText;

      try {
        console.error("[apiFetch] non-2xx", { code, reqId, status: res.status, url: res.url });
      } catch {}

      throw err;
    }

    return res;
  }

  // Should be unreachable (loop always returns or throws)
  throw new Error("apiFetch failed after retry");
}
